import { CircleAlert, LoaderCircle, MapPin, ShoppingBag } from 'lucide-preact'
import { text } from '#contract'
import type { LocalizedText } from '#contract'
import { useClient } from '../../../app/client-context.ts'
import { durationText } from '../../../i18n/eta.ts'
import { useT } from '../../../i18n/index.ts'
import styles from './checkout.module.css'
import products from './products.module.css'
import { useShop } from './shop-context.ts'
import type { Refusal } from './shop-state.ts'
import { useShopState } from './shop-state.ts'
import { ProductIcon, Stepper } from './stepper.tsx'
import { orderLines } from './summary.ts'

const REFUSALS: Readonly<Record<Refusal, string>> = {
  funds: 'shop.refused.funds',
  empty: 'shop.refused.empty',
  unavailable: 'shop.unavailable',
  unknown: 'shop.refused.unknown',
}

/**
 * The basket: where it goes and when, its lines with steppers, subtotal, fee, total and the
 * balance after; "Place order" (disabled, saying why, when the balance is short) and "Empty
 * basket". Placing sends the lines with a fresh request id; the answer is settled by
 * `ShopState`, which opens the tracker or leaves a refusal here.
 */
export function BasketView() {
  const t = useT()
  const client = useClient()
  const shops = useShopState()
  const { os, vendor, wallet, basket, summary, money, nav } = useShop()
  const { pending, refused } = shops.checkout(vendor.id).value

  if (summary.items === 0) {
    return (
      <div class={styles.empty}>
        <ShoppingBag aria-hidden="true" />
        <p>{t(text('shop.empty'))}</p>
        <button
          type="button"
          class={styles.secondary}
          onClick={() => {
            nav.reset()
          }}
        >
          {t(text('shop.keepShopping'))}
        </button>
      </div>
    )
  }

  const short = summary.totalCents - wallet.cents
  const total = money(summary.totalCents)
  let problem: LocalizedText | null = null
  if (refused) problem = text(REFUSALS[refused])
  else if (short > 0) problem = text('shop.notEnough', { money: money(short) })
  const place = (): void => {
    const requestId = shops.begin(vendor.id, os.form)
    client?.shops.order(requestId, vendor.id, orderLines(summary))
  }

  return (
    <div class={styles.sheet}>
      <div class={styles.box}>
        <div class={styles.address}>
          <span class={styles.addressIcon}>
            <MapPin aria-hidden="true" />
          </span>
          <span>
            <span class={styles.label}>{t(text('shop.deliverTo'))}</span>
            <span class={styles.value}>
              {t(text('shop.homeIn', { time: durationText(vendor.deliveryMinutes) }))}
            </span>
          </span>
        </div>
      </div>
      <div class={styles.box}>
        {summary.lines.map(({ product, count }) => (
          <div key={product.item} class={styles.line}>
            <span class={products.pic}>
              <ProductIcon product={product} class={products.picIcon} />
            </span>
            <span class={products.info}>
              <span class={products.name}>{t(product.name)}</span>
              <span class={styles.lineTotal}>{money(product.priceCents * count)}</span>
            </span>
            <Stepper product={product} />
          </div>
        ))}
      </div>
      <div class={styles.box}>
        <Sum label="shop.subtotal" value={money(summary.subtotalCents)} />
        <Sum label="shop.fee" value={money(vendor.feeCents)} />
        <Sum label="shop.total" value={total} total />
        <Sum
          label={short > 0 ? 'shop.yourBalance' : 'shop.balanceAfter'}
          value={money(short > 0 ? wallet.cents : -short)}
          short={short > 0}
        />
      </div>
      <button
        type="button"
        class={styles.clear}
        onClick={() => {
          basket.clear()
        }}
      >
        {t(text('shop.emptyBasket'))}
      </button>
      <footer class={styles.foot}>
        {problem && (
          <p class={styles.problem} role="status">
            <CircleAlert aria-hidden="true" />
            {t(problem)}
          </p>
        )}
        <button
          type="button"
          class={styles.place}
          disabled={short > 0}
          aria-disabled={pending !== null || undefined}
          onClick={() => {
            if (pending === null) place()
          }}
        >
          {pending ? (
            <>
              <LoaderCircle class={styles.spinner} aria-hidden="true" />
              {t(text('shop.placing'))}
            </>
          ) : (
            t(text('shop.placeOrder', { total }))
          )}
        </button>
      </footer>
    </div>
  )
}

interface SumProps {
  readonly label: string
  readonly value: string
  readonly total?: boolean
  readonly short?: boolean
}

function Sum({ label, value, total = false, short = false }: SumProps) {
  const t = useT()
  return (
    <p class={styles.sum} data-total={total || undefined} data-short={short || undefined}>
      <span>{t(text(label))}</span>
      <span>{value}</span>
    </p>
  )
}
